import { useQuery } from '@tanstack/react-query';
import {
  type Alias_WhatsAppPhoneListQueryParams as WhatsAppPhonesListParams,
  type IntegrationAddress,
  MessagingWhatsappPhonesService,
  type WhatsAppPhoneListResponse,
} from 'src/api/generated';
import { type ApiResponse, useMemoizedArrayData } from 'src/hooks/api/api';
import { useSupportTenant } from 'src/modules/shared/contexts/support-tenant-context';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';

interface UseWhatsAppPhoneListResponse extends ApiResponse {
  phones: IntegrationAddress[];
}

interface UseWhatsAppPhoneListParams extends WhatsAppPhonesListParams {
  enabled?: boolean;
}

export const useWhatsAppPhones = ({
  enabled = true,
  ...params
}: UseWhatsAppPhoneListParams = {}): UseWhatsAppPhoneListResponse => {
  const { assumedTenantId } = useSupportTenant();
  const { user: currentUser } = useCurrentUser();
  const effectiveTenantId =
    params.tenantId ?? assumedTenantId ?? currentUser?.tenantId ?? null;
  const fetchPhones = async (): Promise<WhatsAppPhoneListResponse> => {
    const { tenantId } = params;

    return await MessagingWhatsappPhonesService.getPhones(tenantId);
  };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['messaging/whatsapp/phones', effectiveTenantId, params],
    queryFn: fetchPhones,
    enabled: enabled && effectiveTenantId !== null,
    select: (data) => {
      const sortByCreationAsc = (
        a: IntegrationAddress,
        b: IntegrationAddress
      ) => {
        const aDate = new Date(a.createdAt).getTime();
        const bDate = new Date(b.createdAt).getTime();
        return aDate - bDate;
      };

      return [...data].sort(sortByCreationAsc);
    },
  });

  const phones = useMemoizedArrayData<IntegrationAddress>(data);

  return {
    loading,
    error,
    phones,
    refetch,
  };
};
